import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Helmet from 'react-helmet';
import type { Iterable } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { Button, LinearProgress } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';

import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { selectCauses, selectERGsandPillars } from 'entities/cause/selectors';
import { Privileges } from 'utils/privileges';
import Cause from 'entities/cause/model';
import { selectUser } from 'entities/user/selectors';
import CausesPageBanner from 'assets/pageBanners/causes.avif';
import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';
import { Features } from 'utils/features';

import CausesTable from './CausesTable';
import { initAction, setSearchAction, addNewAction } from './types';
import { selectLoading, selectSearch } from './selectors';
import reducer from './reducer';
import epics from './epics';
import { MigrateERGsToCommunitiesModal } from './MigrateERGsToCommunitiesModal/MigrateERGsToCommunitiesModal';

const key = 'causesPage';

const CausesPage: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const search = useSelector(selectSearch);
  const user = useSelector(selectUser);
  const causes: Iterable<string, Cause> = useSelector(selectCauses);
  const causesERGPillar: Iterable<string, Cause> = useSelector(selectERGsandPillars);

  const isAdmin = user?.isAdmin;

  const isCommunitiesEnabled = user?.hasFeature(Features.COMMUNITIES);

  const [showMigrateERGsModal, setShowMigrateERGsModal] = useState(false);

  const loading = useSelector(selectLoading) || !user;
  if (user && (!user.isEmployee || !user.hasPrivilege(Privileges.CAUSE_MANAGE))) {
    history.replace('/');
  }

  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });

  let causesToDisplay: Iterable<string, Cause>;
  if (search) {
    causesToDisplay = causes.filter(
      (eachCauseToDisplay?: Cause) =>
        !!eachCauseToDisplay && eachCauseToDisplay.name.toLowerCase().includes(search.toLowerCase())
    );
  } else {
    causesToDisplay = !user?.isAdmin ? causesERGPillar : causes;
  }
  if (isCommunitiesEnabled) {
    causesToDisplay = causesToDisplay.filter((cause) => !!cause && cause?.type !== 'ERG');
  }

  useEffect(() => {
    dispatch(initAction());
  }, []);

  const title = 'Cause Areas';

  return (
    <>
      <Helmet title={title} />
      <PageBannerContainer color="#FCE9FF" backgroundImage={CausesPageBanner}>
        <h1 style={{ flexGrow: 1 }}>{title}</h1>
        <div style={{ display: 'flex', gap: 4 }}>
          {isAdmin && isCommunitiesEnabled && (
            <Button
              variant="outlined"
              type="button"
              onClick={() => {
                setShowMigrateERGsModal(true);
              }}
            >
              Migrate ERGs To Communities
            </Button>
          )}

          <Button
            variant="outlined"
            type="button"
            onClick={() => {
              dispatch(addNewAction());
            }}
          >
            Add new Cause Area
          </Button>
        </div>
      </PageBannerContainer>
      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <Grid container style={{ marginBottom: 20 }}>
            <Grid item xs={6}>
              <TextField
                type="search"
                label="Search"
                helperText={`Search for the cause you'd like to find`}
                fullWidth
                onChange={(searchField) => {
                  // this.filterAndSortList(radioFilter, match(deed.target.value));
                  dispatch(setSearchAction(searchField.target.value));
                }}
              />
            </Grid>
            <Grid item xs={6} />
          </Grid>
          {causes && <CausesTable causes={causesToDisplay} />}
        </>
      )}
      {showMigrateERGsModal && (
        <MigrateERGsToCommunitiesModal
          onClickClose={() => {
            setShowMigrateERGsModal(false);
          }}
        />
      )}
    </>
  );
};

export default CausesPage;
